import React, { useState } from 'react';

function Section({ title, onEdit, stepNum, children }) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900 text-sm">{title}</h3>
        <button
          onClick={() => onEdit(stepNum)}
          className="text-xs text-[#1e3a5f] font-medium hover:underline"
        >
          Edit
        </button>
      </div>
      <div className="px-4 py-4 space-y-2">
        {children}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm gap-4">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className="text-gray-900 text-right font-medium">{value || '—'}</span>
    </div>
  );
}

function SubHeader({ children }) {
  return (
    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide pt-3 pb-1 border-t border-gray-100 first:border-t-0 first:pt-0">
      {children}
    </div>
  );
}

// ---- Type-specific Coverage Detail renderers ----

function AutoReview({ data }) {
  const vehicles = data.vehicles || [];
  const drivers = data.drivers || [];
  const cov = data.coverage_preferences || {};
  return (
    <>
      {vehicles.map((v, i) => (
        <React.Fragment key={i}>
          <SubHeader>Vehicle {i + 1}</SubHeader>
          <Row label="Year / Make / Model" value={[v.year, v.make, v.model].filter(Boolean).join(' ')} />
          {v.vin && <Row label="VIN" value={v.vin} />}
          {v.primary_use && <Row label="Primary Use" value={v.primary_use} />}
          {v.annual_mileage && <Row label="Annual Mileage" value={v.annual_mileage} />}
          {v.ownership && <Row label="Ownership" value={v.ownership} />}
          {v.lienholder && <Row label="Lienholder" value={v.lienholder} />}
        </React.Fragment>
      ))}
      {drivers.map((d, i) => (
        <React.Fragment key={i}>
          <SubHeader>Driver {i + 1}</SubHeader>
          {d.full_name && <Row label="Full Name" value={d.full_name} />}
          {d.dob && <Row label="Date of Birth" value={d.dob} />}
          {d.license_number && <Row label="License Number" value={d.license_number} />}
          {d.license_state && <Row label="License State" value={d.license_state} />}
          {d.year_first_licensed && <Row label="Year First Licensed" value={d.year_first_licensed} />}
          {d.relationship && <Row label="Relationship" value={d.relationship} />}
        </React.Fragment>
      ))}
      {Object.keys(cov).length > 0 && (
        <>
          <SubHeader>Coverage Preferences</SubHeader>
          {cov.liability_limits && <Row label="Liability Limits" value={cov.liability_limits} />}
          {cov.deductible && <Row label="Deductible" value={cov.deductible} />}
          <Row label="Comprehensive" value={cov.comprehensive || '—'} />
          <Row label="Collision" value={cov.collision || '—'} />
          <Row label="Uninsured Motorist" value={cov.uninsured_motorist || '—'} />
        </>
      )}
    </>
  );
}

function HomeReview({ data }) {
  return (
    <>
      {data.property_address && <Row label="Property Address" value={data.property_address} />}
      <Row label="Year Built" value={data.year_built} />
      <Row label="Square Footage" value={data.sq_footage} />
      {data.stories && <Row label="Number of Stories" value={data.stories} />}
      {data.construction_type && <Row label="Construction Type" value={data.construction_type} />}
      {data.roof_material && <Row label="Roof Material" value={data.roof_material} />}
      {data.roof_year && <Row label="Roof Last Replaced" value={data.roof_year} />}
      {data.occupancy && <Row label="Occupancy" value={data.occupancy} />}
      <SubHeader>Property Features</SubHeader>
      {data.pool && <Row label="Swimming Pool" value={data.pool} />}
      {data.trampoline && <Row label="Trampoline" value={data.trampoline} />}
      {data.dog && <Row label="Dog on Premises" value={data.dog} />}
      {data.dog_breed && <Row label="Dog Breed" value={data.dog_breed} />}
      <SubHeader>Coverage & Mortgage</SubHeader>
      <Row label="Estimated Dwelling Replacement Value" value={data.dwelling_value ? `$${data.dwelling_value}` : null} />
      {data.mortgage_lender && <Row label="Mortgage Lender" value={data.mortgage_lender} />}
      {data.loan_number && <Row label="Loan Number" value={data.loan_number} />}
      {(data.burglar_alarm || data.smoke_detectors || data.sprinkler) && (
        <>
          <SubHeader>Safety Features</SubHeader>
          {data.burglar_alarm && <Row label="Burglar Alarm" value={data.burglar_alarm} />}
          {data.smoke_detectors && <Row label="Smoke Detectors" value={data.smoke_detectors} />}
          {data.sprinkler && <Row label="Sprinkler System" value={data.sprinkler} />}
        </>
      )}
    </>
  );
}

function RentersReview({ data }) {
  return (
    <>
      {data.property_address && <Row label="Property Address" value={data.property_address} />}
      {data.unit_number && <Row label="Unit Number" value={data.unit_number} />}
      {data.building_type && <Row label="Building Type" value={data.building_type} />}
      {data.roommates && <Row label="Roommates" value={data.roommates} />}
      {data.property_value && <Row label="Personal Property Value" value={`$${data.property_value}`} />}
      {data.high_value_items && <Row label="High-Value Items" value={data.high_value_items} />}
      {data.high_value_description && <Row label="High-Value Description" value={data.high_value_description} />}
      {data.liability_limit && <Row label="Liability Limit" value={data.liability_limit} />}
    </>
  );
}

function BOPReview({ data }) {
  return (
    <>
      {data.location_address && <Row label="Business Location Address" value={data.location_address} />}
      {data.year_built && <Row label="Year Built" value={data.year_built} />}
      {data.sq_footage && <Row label="Sq Ft Occupied" value={data.sq_footage} />}
      {data.construction_type && <Row label="Construction Type" value={data.construction_type} />}
      {data.occupancy && <Row label="Occupancy" value={data.occupancy} />}
      <SubHeader>Business Financials</SubHeader>
      {data.annual_revenue && <Row label="Annual Gross Revenue" value={`$${data.annual_revenue}`} />}
      {data.employees && <Row label="Number of Employees" value={data.employees} />}
      <SubHeader>Coverage Limits</SubHeader>
      {data.building_limit && <Row label="Building Limit" value={`$${data.building_limit}`} />}
      {data.bpp_limit && <Row label="Contents / BPP Limit" value={`$${data.bpp_limit}`} />}
      {data.liability_limit && <Row label="Liability Limit" value={data.liability_limit} />}
      {(data.sprinkler || data.alarm) && (
        <>
          <SubHeader>Safety</SubHeader>
          {data.sprinkler && <Row label="Sprinkler System" value={data.sprinkler} />}
          {data.alarm && <Row label="Alarm System" value={data.alarm} />}
        </>
      )}
    </>
  );
}

function CGLReview({ data }) {
  return (
    <>
      {data.occurrence_limit && <Row label="Each Occurrence Limit" value={data.occurrence_limit} />}
      {data.aggregate_limit && <Row label="General Aggregate Limit" value={data.aggregate_limit} />}
      {data.coverage_trigger && <Row label="Coverage Trigger" value={data.coverage_trigger} />}
      <SubHeader>Business Financials</SubHeader>
      {data.annual_revenue && <Row label="Annual Gross Revenue" value={`$${data.annual_revenue}`} />}
      {data.annual_payroll && <Row label="Annual Payroll" value={`$${data.annual_payroll}`} />}
      {data.employees && <Row label="Number of Employees" value={data.employees} />}
      <SubHeader>Operations</SubHeader>
      {data.subcontracted && <Row label="Subcontracted Work" value={data.subcontracted} />}
      {data.sub_pct && <Row label="% Subcontracted" value={data.sub_pct} />}
      {data.subs_have_gl && <Row label="Subs Carry Own GL" value={data.subs_have_gl} />}
      {data.liquor && <Row label="Liquor on Premises" value={data.liquor} />}
      {data.professional_services && <Row label="Professional Services" value={data.professional_services} />}
    </>
  );
}

function WCReview({ data }) {
  const classes = data.class_codes || [];
  return (
    <>
      {data.el_limit && <Row label="Employer's Liability Limit" value={data.el_limit} />}
      {data.total_payroll && <Row label="Total Annual Payroll" value={`$${data.total_payroll}`} />}
      {data.ft_employees && <Row label="Full-Time Employees" value={data.ft_employees} />}
      {data.pt_employees && <Row label="Part-Time Employees" value={data.pt_employees} />}
      {classes.length > 0 && (
        <>
          <SubHeader>Job Classes</SubHeader>
          {classes.map((c, i) => (
            <Row key={i} label={c.description || `Class ${i + 1}`} value={c.payroll ? `$${c.payroll}` : null} />
          ))}
        </>
      )}
      {data.officers_included && <Row label="Officers Included" value={data.officers_included} />}
      {data.xmod && <Row label="Experience Mod (X-Mod)" value={data.xmod} />}
    </>
  );
}

function CommercialAutoReview({ data }) {
  const vehicles = data.vehicles || [];
  const drivers = data.drivers || [];
  return (
    <>
      {vehicles.map((v, i) => (
        <React.Fragment key={i}>
          <SubHeader>Vehicle {i + 1}</SubHeader>
          <Row label="Year / Make / Model" value={[v.year, v.make, v.model].filter(Boolean).join(' ')} />
          {v.vin && <Row label="VIN" value={v.vin} />}
          {v.garaging_zip && <Row label="Garaging ZIP" value={v.garaging_zip} />}
        </React.Fragment>
      ))}
      <SubHeader>Coverage</SubHeader>
      {data.liability_limit && <Row label="Liability CSL Limit" value={data.liability_limit} />}
      {data.hired_auto && <Row label="Hired Auto" value={data.hired_auto} />}
      {data.non_owned && <Row label="Non-Owned Auto" value={data.non_owned} />}
      {data.hazmat && <Row label="Hazardous Materials" value={data.hazmat} />}
      {data.dot_filing && <Row label="DOT/ICC Filing Required" value={data.dot_filing} />}
      {drivers.map((d, i) => (
        <React.Fragment key={i}>
          <SubHeader>Driver {i + 1}</SubHeader>
          {d.full_name && <Row label="Full Name" value={d.full_name} />}
          {d.dob && <Row label="Date of Birth" value={d.dob} />}
          {d.license && <Row label="License #" value={d.license} />}
          {d.state && <Row label="License State" value={d.state} />}
          {d.year_licensed && <Row label="Year First Licensed" value={d.year_licensed} />}
        </React.Fragment>
      ))}
    </>
  );
}

function UmbrellaReview({ data }) {
  return (
    <>
      {data.umbrella_limit && <Row label="Umbrella Limit" value={data.umbrella_limit} />}
      <SubHeader>Underlying Policies</SubHeader>
      {data.underlying_cgl && <Row label="Underlying CGL" value={data.underlying_cgl} />}
      {data.underlying_auto && <Row label="Underlying Auto" value={data.underlying_auto} />}
      {data.underlying_el && <Row label="Underlying Employer's Liability" value={data.underlying_el} />}
      <SubHeader>Additional Information</SubHeader>
      {data.foreign_ops && <Row label="Foreign Operations" value={data.foreign_ops} />}
      {data.known_claims && <Row label="Claims Exceeding Underlying Limits" value={data.known_claims} />}
    </>
  );
}

const COVERAGE_DATA_KEY = {
  'Personal Auto': 'form_data_auto',
  'Homeowners': 'form_data_home',
  'Renters': 'form_data_renters',
  'Business Owners Policy': 'form_data_bop',
  'Commercial General Liability': 'form_data_cgl',
  'Workers Compensation': 'form_data_wc',
  'Commercial Auto': 'form_data_commercial_auto',
  'Umbrella': 'form_data_umbrella',
};

const COVERAGE_REVIEW = {
  'Personal Auto': AutoReview,
  'Homeowners': HomeReview,
  'Renters': RentersReview,
  'Business Owners Policy': BOPReview,
  'Commercial General Liability': CGLReview,
  'Workers Compensation': WCReview,
  'Commercial Auto': CommercialAutoReview,
  'Umbrella': UmbrellaReview,
};

export default function QuoteStepReview({ form, files, isCommercial, submitting, onBack, onSubmit, onEditStep }) {
  const [consented, setConsented] = useState(false);

  const coverageKey = COVERAGE_DATA_KEY[form.coverage_type];
  const coverageData = coverageKey ? (form[coverageKey] || {}) : {};
  const CoverageReviewComponent = COVERAGE_REVIEW[form.coverage_type];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2">Review Your Information</h2>
        <p className="text-gray-500">Please review everything below before submitting.</p>
      </div>

      <Section title="Coverage Type" onEdit={onEditStep} stepNum={1}>
        <Row label="Coverage" value={form.coverage_type} />
      </Section>

      <Section title="Applicant Information" onEdit={onEditStep} stepNum={2}>
        <Row label="Name" value={`${form.first_name} ${form.last_name}`} />
        <Row label="Email" value={form.email} />
        <Row label="Phone" value={form.phone} />
        {!isCommercial && <Row label="Date of Birth" value={form.date_of_birth} />}
        {isCommercial && (
          <>
            <Row label="Business Name" value={form.business_legal_name} />
            {form.business_dba && <Row label="DBA" value={form.business_dba} />}
            <Row label="Entity Type" value={form.business_entity_type} />
            {form.business_description && <Row label="Business Description" value={form.business_description} />}
          </>
        )}
        <Row label="Address" value={`${form.address_street}, ${form.address_city}, ${form.address_state} ${form.address_zip}`} />
      </Section>

      <Section title="Coverage Details" onEdit={onEditStep} stepNum={3}>
        {CoverageReviewComponent
          ? <CoverageReviewComponent data={coverageData} />
          : <Row label="Coverage Type" value={form.coverage_type} />
        }
      </Section>

      <Section title="Loss History" onEdit={onEditStep} stepNum={4}>
        <Row label="Prior Claims" value={form.has_prior_losses ? 'Yes' : 'No'} />
        {form.has_prior_losses && form.loss_history.map((l, i) => (
          <Row key={i} label={`Loss ${i + 1}`} value={`${l.date} — ${l.type} — $${l.amount}`} />
        ))}
        <Row label="Prior Cancellation" value={form.prior_cancellation ? 'Yes' : 'No'} />
        {form.prior_cancellation && <Row label="Explanation" value={form.prior_cancellation_explanation} />}
      </Section>

      <Section title="Prior Insurance" onEdit={onEditStep} stepNum={5}>
        <Row label="Prior Carrier" value={form.prior_carrier} />
        <Row label="Policy Expiration" value={form.prior_policy_expiration} />
        <Row label="Years Continuously Insured" value={form.years_continuously_insured} />
      </Section>

      {files.length > 0 && (
        <Section title="Uploaded Documents" onEdit={onEditStep} stepNum={6}>
          {files.map((f, i) => (
            <Row key={i} label={`File ${i + 1}`} value={f.name} />
          ))}
        </Section>
      )}

      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={consented}
            onChange={e => setConsented(e.target.checked)}
            className="mt-0.5 accent-[#1e3a5f]"
          />
          <span className="text-sm text-gray-600 leading-relaxed">
            By submitting this form, you authorize Pinoy General Insurance Services to contact you regarding your insurance quote. Your information will never be sold to third parties.
          </span>
        </label>
      </div>

      <div className="space-y-3">
        <button
          onClick={onSubmit}
          disabled={!consented || submitting}
          className="w-full bg-[#1e3a5f] hover:bg-[#162d4a] disabled:bg-gray-300 text-white font-semibold py-4 rounded-xl transition-colors text-lg"
        >
          {submitting ? 'Submitting...' : 'Submit My Quote Request'}
        </button>
        <button
          onClick={onBack}
          className="w-full text-gray-500 hover:text-gray-700 text-sm font-medium py-2 transition-colors"
        >
          ← Back
        </button>
      </div>
    </div>
  );
}
